import { readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

/** §31 (HANDOVER_PLAN §2b): the school admin's approved-email list and join requests. */

export type JoinPolicy = "open" | "approved_only";

export type JoinSettings = {
  policy: JoinPolicy;
  studentCount: number;
  unlistedStudentCount: number;
  /** S3 F-3: current students with no verified email (signed out by the switch, F-13). */
  unverifiedStudentCount?: number;
  approvedCount: number;
  pendingRequestCount: number;
};

export type ApprovedEmail = { id: string; email: string; name: string | null; createdAt: string };

export type AddApprovedEmailsResult = {
  added: number;
  alreadyListed: number;
  invalidCount: number;
  invalid: string[];
};

export type JoinRequestStatus = "pending" | "approved" | "declined";

export type JoinRequest = {
  id: string;
  email: string;
  name: string | null;
  message: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  status: JoinRequestStatus;
  requestCount: number;
  firstRequestedAt: string;
  lastRequestedAt: string;
  decidedAt: string | null;
};

export type PageMeta = { total: number; page: number; limit: number };

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw await readApiErrorResponse(res);
  return (await res.json()) as T;
}

export async function fetchJoinSettings(): Promise<JoinSettings> {
  return (await json<{ data: JoinSettings }>(await apiFetch("/v1/tenant/join-settings"))).data;
}

export type JoinPolicySaved = JoinSettings & {
  addedStudents: number;
  /** S3 F-13: unverified students signed out by the switch to approved-only. */
  signedOutUnverified?: number;
};

/** S3 F-3: `force` switches to approved-only although no email sender is configured. */
export async function saveJoinPolicy(
  policy: JoinPolicy,
  options: { force?: boolean } = {},
): Promise<JoinPolicySaved> {
  const res = await apiFetch("/v1/tenant/join-settings", {
    method: "PUT",
    body: JSON.stringify(options.force ? { policy, force: true } : { policy }),
  });
  return (await json<{ data: JoinPolicySaved }>(res)).data;
}

export async function fetchApprovedEmails(input: {
  q?: string;
  page: number;
  limit?: number;
}): Promise<{ entries: ApprovedEmail[]; meta: PageMeta }> {
  const params = new URLSearchParams({
    page: String(input.page),
    limit: String(input.limit ?? 50),
  });
  if (input.q) params.set("q", input.q);
  const body = await json<{ data: { entries: ApprovedEmail[] }; meta: PageMeta }>(
    await apiFetch(`/v1/tenant/approved-emails?${params.toString()}`),
  );
  return { entries: body.data.entries, meta: body.meta };
}

export async function addApprovedEmails(text: string): Promise<AddApprovedEmailsResult> {
  const res = await apiFetch("/v1/tenant/approved-emails", {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  return (await json<{ data: AddApprovedEmailsResult }>(res)).data;
}

export async function removeApprovedEmail(
  id: string,
): Promise<{ email: string; sessionsEnded: number }> {
  const res = await apiFetch(`/v1/tenant/approved-emails/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  return (await json<{ data: { email: string; sessionsEnded: number } }>(res)).data;
}

export async function fetchJoinRequests(input: {
  status: JoinRequestStatus;
  page: number;
  limit?: number;
}): Promise<{ requests: JoinRequest[]; pendingCount: number; meta: PageMeta }> {
  const params = new URLSearchParams({
    status: input.status,
    page: String(input.page),
    limit: String(input.limit ?? 50),
  });
  const body = await json<{
    data: { requests: JoinRequest[]; pendingCount: number };
    meta: PageMeta;
  }>(await apiFetch(`/v1/tenant/join-requests?${params.toString()}`));
  return { ...body.data, meta: body.meta };
}

export async function decideJoinRequest(
  id: string,
  decision: "approve" | "decline",
): Promise<{ request: JoinRequest; emailQueued?: boolean; emailWaiting?: boolean }> {
  const res = await apiFetch(`/v1/tenant/join-requests/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ decision }),
  });
  return (
    await json<{ data: { request: JoinRequest; emailQueued?: boolean; emailWaiting?: boolean } }>(
      res,
    )
  ).data;
}
